import React, { useEffect, useMemo, useState } from 'react';
import { mapPlans } from '../../../common/utils/mapPlans';
import CustomSpinner from '../../../common/CustomSpinner';
import TextBase from '../../../common/text';
import { Colors } from '../../../themes/Colors';
import { translate } from '../../../helper';
import CheckIcon from '@mui/icons-material/Check';
import PlansGrid from '../../../common/subscription-card/PlansGrid';
import { subscriptionType } from '../const';
import { useScreenSubscription } from '../screenProviderLocationAccount';
import { ChildAccountsDetailDynamic } from '../../../modules/child-accounts-detail/childAccountsDetailDynamic';

const StepItem = ({ state, label, number }) => {
  const isActive = state === 'active';
  const isCompleted = state === 'completed';
  const circleStyles = {
    width: 'calc(var(--fs-xl) * 2)',
    height: 'calc(var(--fs-xl) * 2)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isCompleted ? '#FFFFFF' : isActive ? Colors.black : '#6B7280',
    background: isCompleted ? Colors.primary : '#FFFFFF',
    border: `calc(var(--fs-xxs) * 0.30075) solid ${isActive || isCompleted ? Colors.primary : '#D1D5DB'}`,
    fontWeight: 700,
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={circleStyles}>
        {isCompleted ? (
          <CheckIcon style={{ fontSize: 'calc(var(--fs-xs) * 1.894736)', color: '#FFFFFF' }} />
        ) : (
          <TextBase size={'calc(var(--fs-xs) * 1.78947)'} fontWeight={600} color={Colors.black}>{number}</TextBase>
        )}
      </div>
      <div style={{ position: 'absolute', top: 'calc(var(--fs-xl) * 2.1526315)', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <TextBase
          fontWeight={isActive ? 600 : 500}
          size={'calc(var(--fs-xs) * 1.473684)'}
          color={isActive ? Colors.black : '#9CA3AF'}
        >
          {label}
        </TextBase>
      </div>
    </div>
  );
};

const Divider = () => (
  <div style={{ width: '100%', height: 1, background: '#E5E7EB', marginBottom: 'calc(var(--sp-md2) * 2.1052631)' }} />
);

const CreateLocation = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [childPlanDetails, setChildPlanDetails] = useState(null);
  const [step, setStep] = useState(1);
  const { goBack, subscriptionProduct: providerSubscriptionProduct, setSelectedSubscription, childAccount } = useScreenSubscription();
  const [isYearlyLocal, setIsYearlyLocal] = useState(false);

  useEffect(() => {
    if (!providerSubscriptionProduct) {
      setChildPlanDetails(null);
      setIsLoading(true);
      return;
    }
    setChildPlanDetails(providerSubscriptionProduct);
    setIsLoading(false);
  }, [providerSubscriptionProduct]);

  const mappedPlans = useMemo(() => mapPlans(childPlanDetails, undefined, childAccount, false), [childPlanDetails, childAccount]);

  const handleContinue = (pickedPlan) => {
    const backendPlan = pickedPlan?.rawSelected;

    const payload = {
      title: pickedPlan?.title || 'locationSubAccount',
      subTitle: pickedPlan?.isYearly ? 'yearlySubscription' : 'monthlySubscription',
      paymentId: backendPlan?.id,
      price: backendPlan?.price || subscriptionType.DEFAULT_VALUE,
      recurringInterval: backendPlan?.recurring_interval || subscriptionType.DEFAULT_VALUE,
      percentageDiscount: backendPlan?.discount || subscriptionType.DEFAULT_VALUE,
      isYearly: !!pickedPlan?.isYearly,
    };
    setSelectedSubscription(payload);
    setStep(2);
  };

  const Header = useMemo(() => {
    const step1State = step === 1 ? 'active' : 'completed';
    const step2State = step === 2 ? 'active' : 'inactive';
    return (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 'calc(var(--sp-lg) * 1.6842105)'}}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 'calc(var(--fs-hero) * 1.315789)', height: 'calc(var(--fs-xxs) * 0.30075)', background: Colors.primary, borderRadius: 'calc(var(--sp-xxs) * 0.5263157)' }} />
              <StepItem number={1} state={step1State} label={translate('choosePlan') || 'Choose Plan'} />
              <div style={{ width: 'calc(var(--fs-hero) * 1.315789)', height: 'calc(var(--fs-xxs) * 0.30075)', background: Colors.primary, borderRadius: 'calc(var(--sp-xxs) * 0.5263157)' }} />
              <div style={{ width: 'calc(var(--fs-hero) * 1.315789)', height: 'calc(var(--fs-xxs) * 0.30075)', background: '#E5E7EB', borderRadius: 'calc(var(--sp-xxs) * 0.5263157)' }} />
              <StepItem number={2} state={step2State} label={translate('locationDetails') || 'Location Details'} />
              <div style={{ width: 'calc(var(--fs-hero) * 1.315789)', height: 'calc(var(--fs-xxs) * 0.30075)', background: '#E5E7EB', borderRadius: 'calc(var(--sp-xxs) * 0.5263157)' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }, [step]);
  return (
    <div>
      <Divider />
      <div style={{ margin: 'auto', width: '90%' }}>
        {Header}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(var(--fs-hero) * 6.315789)' }}>
            <CustomSpinner fullPage/>
          </div>
        ) : (
          <>
            {step === 1 && (
              <div style={{ marginTop: 'calc(var(--sp-lg) * 2.526315)' }}>
                <PlansGrid
                  plans={mappedPlans ? mappedPlans.filter(p => (p.key || '').toLowerCase() !== 'free trial') : []}
                  isYearly={isYearlyLocal}
                  initialIsYearly={isYearlyLocal}
                  onIsYearlyChange={setIsYearlyLocal}
                  onPurchase={handleContinue}
                  buttonText1={translate('cancel') || 'Cancel'}
                  buttonText2={translate('continue') || 'Continue'}
                  onButton1Click={goBack}
                  onButton2Click={handleContinue}
                  headerTitle={"choosePlanTitle"}
                />
              </div>
            )}

            {step === 2 && (
              <div style={{ marginTop: 'calc(var(--sp-lg) * 1.2631578)' }}>
                <div style={{ padding: 0 }}>
                  <ChildAccountsDetailDynamic />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreateLocation;
